/**
 * Web shim for react-native-maps using Leaflet.
 * Renders an interactive map in the browser without a Google Maps API key.
 */
import React, { forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import { View, ViewStyle } from 'react-native';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

type LatLng = { latitude: number; longitude: number };
type Region = LatLng & { latitudeDelta: number; longitudeDelta: number };

// Minimal MapView shim
const MapView = forwardRef<any, {
  style?: ViewStyle | ViewStyle[];
  initialRegion?: Region;
  showsUserLocation?: boolean;
  followsUserLocation?: boolean;
  scrollEnabled?: boolean;
  zoomEnabled?: boolean;
  onLongPress?: (e: any) => void;
  onPress?: (e: any) => void;
  children?: React.ReactNode;
}>(function MapView(props, ref) {
  const mapDivRef = useRef<HTMLDivElement>(null);
  const leafletRef = useRef<any>(null);
  const userMarkerRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({
    animateToRegion: ({ latitude, longitude }: LatLng) => {
      leafletRef.current?.setView([latitude, longitude]);
    },
    animateCamera: ({ center }: { center: LatLng }) => {
      leafletRef.current?.setView([center.latitude, center.longitude]);
    },
  }));

  useEffect(() => {
    if (!mapDivRef.current || !L) return;
    const { initialRegion } = props;
    const center: [number, number] = initialRegion
      ? [initialRegion.latitude, initialRegion.longitude]
      : [37.7749, -122.4194];
    const map = L.map(mapDivRef.current, { zoomControl: true }).setView(center, 15);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
    }).addTo(map);

    if (props.onLongPress) {
      // Simulate mobile long-press: fire after 500ms hold, cancel on move/release
      let holdTimer: ReturnType<typeof setTimeout> | null = null;
      let holdLatLng: any = null;
      map.on('mousedown', (e: any) => {
        holdLatLng = e.latlng;
        holdTimer = setTimeout(() => {
          props.onLongPress?.({ nativeEvent: { coordinate: { latitude: holdLatLng.lat, longitude: holdLatLng.lng } } });
          holdTimer = null;
        }, 500);
      });
      map.on('mouseup mousemove', () => {
        if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
      });
      // Keep right-click as a secondary trigger for power users
      map.on('contextmenu', (e: any) => {
        if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
        props.onLongPress?.({ nativeEvent: { coordinate: { latitude: e.latlng.lat, longitude: e.latlng.lng } } });
      });
    }

    if (props.onPress) {
      map.on('click', (e: any) => {
        props.onPress?.({ nativeEvent: { coordinate: { latitude: e.latlng.lat, longitude: e.latlng.lng } } });
      });
    }

    if (props.showsUserLocation && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        const latlng: [number, number] = [pos.coords.latitude, pos.coords.longitude];
        userMarkerRef.current = L.circleMarker(latlng, { radius: 8, color: '#1a73e8', fillColor: '#1a73e8', fillOpacity: 1 })
          .bindPopup('You are here').addTo(map);
      });
    }

    leafletRef.current = map;
    return () => { map.remove(); };
  }, []);

  // Expose map instance for children via context
  return (
    <MapContext.Provider value={leafletRef}>
      <View style={[{ flex: 1, minHeight: 200 }, props.style as any]}>
        <div ref={mapDivRef} style={{ height: '100%', width: '100%', minHeight: 200 }} />
      </View>
      {props.children}
    </MapContext.Provider>
  );
});

export const MapContext = React.createContext<React.MutableRefObject<any>>({ current: null });

// Marker shim — adds a Leaflet marker when inside MapView
export function Marker(props: {
  coordinate: LatLng;
  title?: string;
  description?: string;
  pinColor?: string;
  draggable?: boolean;
  onDragEnd?: (e: any) => void;
  onCalloutPress?: () => void;
}) {
  const mapRef = React.useContext(MapContext);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !L) return;
    const icon = L.divIcon({
      html: `<div style="width:14px;height:14px;border-radius:50%;background:${props.pinColor ?? '#ea4335'};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5);cursor:${props.draggable ? 'grab' : 'pointer'}"></div>`,
      className: '', iconAnchor: [7, 7],
    });
    const marker = L.marker([props.coordinate.latitude, props.coordinate.longitude], {
      icon,
      draggable: props.draggable ?? false,
    }).addTo(map);
    if (props.title) {
      marker.bindPopup(`<b>${props.title}</b>${props.description ? `<br>${props.description}` : ''}`);
      if (props.onCalloutPress) marker.on('popupopen', props.onCalloutPress);
    }
    if (props.draggable && props.onDragEnd) {
      marker.on('dragend', (e: any) => {
        const { lat, lng } = e.target.getLatLng();
        props.onDragEnd?.({ nativeEvent: { coordinate: { latitude: lat, longitude: lng } } });
      });
    }
    return () => { marker.remove(); };
  }, [props.coordinate.latitude, props.coordinate.longitude, props.pinColor, props.draggable, mapRef]);
  return null;
}

// Polyline shim
export function Polyline(props: {
  coordinates: LatLng[];
  strokeColor?: string;
  strokeWidth?: number;
}) {
  const mapRef = React.useContext(MapContext);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !L || props.coordinates.length < 2) return;
    const latlngs = props.coordinates.map((c) => [c.latitude, c.longitude] as [number, number]);
    const line = L.polyline(latlngs, { color: props.strokeColor ?? '#1a73e8', weight: props.strokeWidth ?? 3 }).addTo(map);
    return () => { line.remove(); };
  }, [props.coordinates, mapRef]);
  return null;
}

export default MapView;
export { MapView };
export type { Region };
